import React, { useState } from 'react';
import { VoucherExperience } from 'services/ItineraryManagerApi/types';
import Editor from 'pureUi/Editor';
import { SlideToggle } from 'ui/SlideToggle';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import { useModal } from 'hooks/useModal';
import { VoucherAddUploadModal } from './VoucherAddUploadModal';
import { CircleIconButton } from 'ui/CircleIconButton';
import FluidButton from 'ui/FluidButton';
import XIcon from 'ui/Icons/tabler/x.component.svg';
import classNames from 'classnames';
import { ProductEditUnderFold } from './common';

interface IVoucherExperienceEditProps {
  experiences: VoucherExperience[];
  voucherUuid: string;
  onVisibilityToggle: (experienceUuid: string, isVisible: boolean) => void;
  onDescriptionChange: (experienceUuid: string, description: string) => void;
  onMoreTextChange: (experienceUuid: string, moreText: string) => void;
  onAddUpload: (experienceUuid: string, uploadData: any) => void;
  onRemoveUpload: (experienceUuid: string, uploadIndex: number) => void;
}

export const VoucherExperienceEdit: React.FC<IVoucherExperienceEditProps> = ({
  experiences,
  voucherUuid,
  onVisibilityToggle,
  onDescriptionChange,
  onMoreTextChange,
  onAddUpload,
  onRemoveUpload,
}) => {
  const [openExperiences, setOpenExperiences] = useState<string[]>([]);
  const addUploadModalOps = useModal<{
    uploadType: 'image' | 'file';
    experienceUuid: string;
  }>();

  if (experiences.length === 0) return null;

  const handleToggle = (experienceUuid: string, isOpen: boolean) => {
    if (isOpen) {
      setOpenExperiences([...openExperiences, experienceUuid]);
    } else {
      setOpenExperiences(openExperiences.filter(uuid => uuid !== experienceUuid));
    }
  };

  return (
    <div className="mb-8">
      <span className="font-pt-sans font-bold text-[17px] text-black mb-4 block">Experiences</span>
      <div>
        {experiences.map(experience => (
          <AccordionStackable
            key={experience.uuid}
            isOpen={openExperiences.includes(experience.uuid)}
            onToggle={isOpen => handleToggle(experience.uuid, isOpen)}
            renderSummary={({ isOpen }) => (
              <span className="flex items-center justify-between w-full pr-4">
                <span
                  className={classNames('text-base font-pt-sans', {
                    'font-bold': isOpen,
                    'font-medium': !isOpen,
                  })}
                >
                  {experience.title}
                </span>
                <SlideToggle
                  id={`toggle-experience-${experience.uuid}`}
                  checked={experience.isVisible}
                  onActivate={() => onVisibilityToggle(experience.uuid, true)}
                  onDeactivate={() => onVisibilityToggle(experience.uuid, false)}
                  labelClassName={!experience.isVisible ? 'bg-gray-20' : 'bg-teal-40'}
                  buttonClassName={!experience.isVisible ? 'bg-gray-100' : 'bg-teal-100'}
                  onClick={e => e.stopPropagation()}
                />
              </span>
            )}
          >
            <div className="space-y-4">
              <ProductEditUnderFold product={experience} />

              <div className="relative">
                <p className="text-sm font-pt-sans text-gray-100 mb-2">Description</p>
                <Editor
                  handleEditorChange={content => onDescriptionChange(experience.uuid, content)}
                  text={experience.description || ''}
                  options={[]}
                  placeHolder="Write experience description..."
                  minHeight="125px"
                  initVals={{
                    autoresize_bottom_margin: 0,
                    min_height: 125,
                  }}
                />
              </div>
              <div className="relative">
                <p className="text-sm font-pt-sans text-gray-100 mb-2">More Text</p>
                <Editor
                  handleEditorChange={content => onMoreTextChange(experience.uuid, content)}
                  text={experience.moreText || ''}
                  options={[]}
                  placeHolder="Write additional experience information..."
                  minHeight="125px"
                  initVals={{
                    autoresize_bottom_margin: 0,
                    min_height: 125,
                  }}
                />
              </div>

              {/* Existing uploads */}
              {experience.uploads.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-pt-sans text-gray-100 mb-2">Attachments</p>
                  <div className="flex gap-2 flex-wrap">
                    {experience.uploads.map((upload, uploadIdx) => (
                      <div key={uploadIdx} className="relative">
                        <span className="bg-gray-10 text-12px text-gray-100 p-10px w-[90px] h-[90px] flex items-center">
                          <span className="w-full line-clamp-3">{upload.displayName}</span>
                        </span>
                        <CircleIconButton
                          className="absolute top-2 right-2"
                          IconComponent={XIcon}
                          iconSize="sm"
                          type="secondary"
                          onClick={() => onRemoveUpload(experience.uuid, uploadIdx)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add attachment button */}
              <div className="mt-4">
                <FluidButton
                  onClick={async () => {
                    const result = await addUploadModalOps.requestConfirmation({
                      uploadType: 'file',
                      experienceUuid: experience.uuid,
                    });
                    if (result) {
                      onAddUpload(experience.uuid, result);
                    }
                  }}
                  type="secondary"
                >
                  Attach File
                </FluidButton>
              </div>
            </div>
          </AccordionStackable>
        ))}
      </div>

      {/* Upload Modal */}
      <VoucherAddUploadModal
        voucherUuid={voucherUuid}
        isOpen={addUploadModalOps.isOpen}
        onClose={addUploadModalOps.handleClose}
        uploadType={addUploadModalOps.modalData?.uploadType || 'file'}
        handleConfirm={addUploadModalOps.handleConfirm}
        modalTitle="Add File to Experience"
        productType="experience"
      />
    </div>
  );
};
